import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Users, ClipboardCheck, UserCircle, Route, Cpu, Rocket, 
  Target, BookOpen, Award, TrendingUp, Briefcase, Lightbulb, CheckCircle,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getToolsByLab } from "@/data/aiRegistry";
import BackNavigation from "@/components/BackNavigation";

/**
 * Workforce Lab Page
 * Individual-level workforce intelligence development
 * Contains the core flow: Assessment → Profile Intelligence → Development Pathway → AI Tool Alignment → Opportunity Intelligence Engine
 */
export default function WorkforceLab() {
  // The Workforce Lab journey steps
  const journeySteps = [
    {
      step: 1,
      icon: ClipboardCheck,
      title: "Assessment",
      description: "Complete the Workforce Intelligence diagnostic to establish your baseline across the five WAIT Theory dimensions.",
      action: "Take Assessment",
      link: "/observatories/global/survey"
    },
    {
      step: 2,
      icon: UserCircle,
      title: "Profile Intelligence",
      description: "Receive your personalized Workforce Intelligence Profile with scores, strengths, and development opportunities.",
      action: "View Your Profile",
      link: "/juswork/dashboard"
    },
    {
      step: 3,
      icon: Route,
      title: "Development Pathway",
      description: "Follow a customized learning and development path based on your profile, goals, and industry context.",
      action: "Explore Pathways",
      link: "/programs"
    },
    {
      step: 4,
      icon: Cpu,
      title: "AI Tool Alignment",
      description: "Access curated AI tools from the Zodiac AI Capability Registry aligned to your development needs and industry.",
      action: "Browse AI Tools",
      link: "/observatories/ai"
    },
    {
      step: 5,
      icon: Rocket,
      title: "Opportunity Intelligence",
      description: "Discover business opportunities, service pathways, and career options matched to your workforce intelligence profile.",
      action: "Discover Opportunities",
      link: "/zodiac-lab/workforce/opportunities"
    }
  ];

  // Specialization tracks (formerly individual ProLab labs)
  const specializations = [
    {
      category: "Enterprise & Business",
      tracks: [
        { name: "AI Entrepreneurship", slug: "ai-entrepreneurship", description: "Build AI-enabled businesses" },
        { name: "Project Management", slug: "project-management", description: "AI-enhanced project delivery" },
        { name: "Sales & Marketing", slug: "sales-marketing", description: "AI-driven growth strategies" }
      ]
    },
    {
      category: "Leadership & Communication",
      tracks: [
        { name: "Leadership AI", slug: "leadership", description: "AI-augmented leadership skills" },
        { name: "Communication AI", slug: "communication", description: "AI-enhanced professional communication" }
      ]
    },
    {
      category: "Quality & Safety",
      tracks: [
        { name: "Total Quality Management", slug: "tqm", description: "AI in quality systems" },
        { name: "Quality Auditing", slug: "quality-auditing", description: "AI-assisted audit processes" },
        { name: "OSHA Safety", slug: "osha-safety", description: "AI in workplace safety" }
      ]
    },
    {
      category: "Industry Specializations",
      tracks: [
        { name: "Logistics & Supply Chain", slug: "logistics", description: "AI in logistics operations" },
        { name: "Construction", slug: "construction", description: "AI in construction management" },
        { name: "Security Operations", slug: "security-level-2", description: "AI-enhanced security" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="workforce-lab-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-6">
                <Link to="/zodiac-lab" className="text-sm text-[#1E3A8A]/60 hover:text-[#1E3A8A]">
                  Zodiac Lab
                </Link>
                <span className="text-[#1E3A8A]/40">/</span>
                <span className="text-sm text-[#1E3A8A] font-medium">Workforce Lab</span>
              </div>
              
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-6">
                <Users className="w-4 h-4" />
                Individual Intelligence
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
                Workforce Lab
              </h1>
              <p className="text-xl font-semibold text-[#1E3A8A] mb-4">
                Develop Your Personal Workforce Intelligence
              </p>
              <p className="text-base text-[#1E3A8A]/80 leading-relaxed mb-8">
                Workforce Lab applies WAIT Theory at the individual level. Assess your current capabilities, 
                build your intelligence profile, follow a personalized development pathway, align with AI tools, 
                and discover opportunities matched to your unique workforce intelligence.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/observatories/global/survey">
                  <Button size="lg" className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                    Start Assessment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/juswork/dashboard">
                  <Button size="lg" variant="outline" className="border-[#991B1B] text-[#991B1B] hover:bg-[#991B1B] hover:text-white">
                    View Dashboard
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="border-[#E5E7EB] bg-gradient-to-br from-[#1E3A8A]/5 to-white">
                <CardContent className="p-8">
                  <h3 className="font-heading text-lg font-bold mb-6" style={{ color: '#991B1B' }}>
                    The Workforce Lab Journey
                  </h3>
                  <div className="space-y-4">
                    {journeySteps.map((step, index) => (
                      <div key={step.step} className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {step.step}
                        </div>
                        <div className="flex-1 pt-1">
                          <h4 className="font-semibold text-[#1E3A8A]">{step.title}</h4>
                          <p className="text-xs text-[#1E3A8A]/60">{step.description.slice(0, 60)}...</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Journey Steps Detail */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Your Development Journey
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Follow the five-stage Workforce Lab journey to develop measurable workforce intelligence.
            </p>
          </div>

          <div className="space-y-8">
            {journeySteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="border-[#E5E7EB] hover:shadow-lg transition-shadow overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid md:grid-cols-12 gap-0">
                      <div className="md:col-span-1 bg-[#1E3A8A] flex items-center justify-center py-6 md:py-0">
                        <span className="text-3xl font-bold text-white">{step.step}</span>
                      </div>
                      <div className="md:col-span-11 p-6 md:p-8">
                        <div className="flex flex-col md:flex-row md:items-center gap-6">
                          <div className="flex-shrink-0">
                            <div className="w-14 h-14 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                              <step.icon className="w-7 h-7 text-[#1E3A8A]" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#991B1B' }}>
                              {step.title}
                            </h3>
                            <p className="text-[#1E3A8A]/80">{step.description}</p>
                          </div>
                          <div className="flex-shrink-0">
                            <Link to={step.link}>
                              <Button className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                                {step.action}
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunity Intelligence Engine Highlight */}
      <section className="py-16 lg:py-24 bg-[#991B1B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center mb-6">
                <Rocket className="w-8 h-8 text-white" />
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                Opportunity Intelligence Engine
              </h2>
              <p className="text-white/90 text-lg mb-6">
                The culmination of your Workforce Lab journey. The Opportunity Intelligence Engine connects 
                your workforce intelligence profile with real-world opportunities.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-white/80 flex-shrink-0" />
                  <span className="text-white/90">Business opportunity recommendations based on your profile</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-white/80 flex-shrink-0" />
                  <span className="text-white/90">Service pathway suggestions matched to your skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-white/80 flex-shrink-0" />
                  <span className="text-white/90">Career pathways aligned with market demand</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-white/80 flex-shrink-0" />
                  <span className="text-white/90">AI tool recommendations for opportunity execution</span>
                </li>
              </ul>
              <Link to="/zodiac-lab/workforce/opportunities">
                <Button size="lg" className="bg-white text-[#991B1B] hover:bg-white/90">
                  Explore Opportunities
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <Card className="bg-white/10 border-white/20">
                <CardContent className="p-6 text-center">
                  <Briefcase className="w-8 h-8 text-white mx-auto mb-3" />
                  <h4 className="font-semibold text-white">Business Ideas</h4>
                  <p className="text-xs text-white/70 mt-1">AI-enabled ventures</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20">
                <CardContent className="p-6 text-center">
                  <Target className="w-8 h-8 text-white mx-auto mb-3" />
                  <h4 className="font-semibold text-white">Service Paths</h4>
                  <p className="text-xs text-white/70 mt-1">Consulting & freelance</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="w-8 h-8 text-white mx-auto mb-3" />
                  <h4 className="font-semibold text-white">Career Growth</h4>
                  <p className="text-xs text-white/70 mt-1">Advancement paths</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20">
                <CardContent className="p-6 text-center">
                  <Cpu className="w-8 h-8 text-white mx-auto mb-3" />
                  <h4 className="font-semibold text-white">AI Execution</h4>
                  <p className="text-xs text-white/70 mt-1">Tools for action</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lab AI Tool Library Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2" style={{ color: '#991B1B' }}>
                Lab AI Tool Library
              </h2>
              <p className="text-lg text-[#1E3A8A]">
                AI tools curated for individual workforce intelligence development.
              </p>
            </div>
            <Link to="/observatories/ai/registry?lab=workforce">
              <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white">
                View All {getToolsByLab('workforce').length} Tools
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {getToolsByLab('workforce').slice(0, 4).map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-md hover:border-[#1E3A8A]/30 transition-all">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-[#1E3A8A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#991B1B] truncate">{tool.name}</h4>
                        <p className="text-xs text-[#1E3A8A]/60">{tool.provider}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#1E3A8A]/70 mb-3 line-clamp-2">
                      {tool.labRelevance.workforce.useCases[0]?.title || tool.labRelevance.workforce.useCases[0]?.description || 'AI tool for workforce development'}
                    </p>
                    <a 
                      href={tool.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline"
                    >
                      Learn more <ExternalLink className="w-3 h-3" />
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialization Tracks */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Specialization Tracks
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Deepen your workforce intelligence in specific domains with our specialized development tracks.
            </p>
          </div>

          <div className="space-y-12">
            {specializations.map((category, catIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: catIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="font-heading text-xl font-bold mb-4" style={{ color: '#1E3A8A' }}>
                  {category.category}
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.tracks.map((track) => (
                    <Link key={track.slug} to={`/zodiac-lab/workforce/tracks/${track.slug}`}>
                      <Card className="border-[#E5E7EB] hover:shadow-md hover:border-[#1E3A8A]/30 transition-all cursor-pointer h-full">
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                              <BookOpen className="w-5 h-5 text-[#1E3A8A]" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-[#991B1B]">{track.name}</h4>
                              <p className="text-sm text-[#1E3A8A]/70">{track.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1E3A8A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Begin Your Workforce Lab Journey
          </h2>
          <p className="text-white/80 mb-8">
            Start with the assessment to establish your workforce intelligence baseline.
          </p>
          <Link to="/observatories/global/survey">
            <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-14 px-8">
              Take the Assessment
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
